import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Crop } from 'lucide-react';
import styled, { css } from 'styled-components';
import colors from 'theme/colors';
import Text from './Text';
import { FullyCentered } from 'styled/alignment/Center';
import { Row } from 'styled/alignment/Row';
import Icon from './Icon';
import ImageCropperModal from './ImageCropperModal';
import breakpoints from 'theme/breakpoints';

export default function ImageUpload({
    onToggleFile,
    onDeleteFile,
    tryAgain,
    refreshTryAgain,
    isEdit,
    resetState,
    product,
    icon,
    // Opcional: quando informado, abre o recorte antes de aceitar a imagem.
    cropAspect,
    cropShape = 'rect',
    cropTitle,
    cropDescription,
    /* Formatos aceitos no formato do react-dropzone v14 ({ mime: ['.ext'] }).
       O default continua sendo a string 'image/*' que já estava aqui, para não
       mudar o comportamento das outras telas que usam este componente. */
    accept = 'image/*',
    maxSizeMb,
    onRejectFile = () => { },
    dropzoneContent,
}) {
    const [dropzoneVisible, setDropzoneVisible] = useState(true);
    const [files, setFiles] = useState([]);
    const [isDelete, setIsDelete] = useState(false);
    const [editImage, setEditImage] = useState(null);
    /* Um PDF restaurado não tem miniatura: guardamos o nome para a pessoa
       reconhecer o arquivo que já anexou. */
    const [editFile, setEditFile] = useState(null);
    const [cropSourceFile, setCropSourceFile] = useState(null);
    const [isCropperOpen, setIsCropperOpen] = useState(false);
    const prevFilesRef = useRef([]);

    const acceptFiles = (acceptedFiles) => {
        const updatedFiles = acceptedFiles.map((file) =>
            Object.assign(file, {
                preview: URL.createObjectURL(file),
            })
        );
        setFiles(updatedFiles);
        setDropzoneVisible(false);
        setEditImage(null);
        onToggleFile(updatedFiles);
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept,
        maxSize: maxSizeMb ? maxSizeMb * 1024 * 1024 : undefined,
        multiple: false,
        onDropRejected: (rejections) => {
            const code = rejections?.[0]?.errors?.[0]?.code;

            if (code === 'file-too-large') {
                onRejectFile(`O arquivo passa de ${maxSizeMb} MB. Envie uma versão menor.`);
                return;
            }

            onRejectFile('Formato não aceito. Envie uma imagem (JPG ou PNG) ou um PDF.');
        },
        onDrop: (acceptedFiles) => {
            if (cropAspect && acceptedFiles.length > 0) {
                setCropSourceFile(acceptedFiles[0]);
                setIsCropperOpen(true);
                return;
            }
            acceptFiles(acceptedFiles);
        },
    });

    const handleCropConfirm = (croppedFile) => {
        setIsCropperOpen(false);
        acceptFiles([croppedFile]);
    };

    const handleCropCancel = () => {
        setIsCropperOpen(false);
        if (files.length === 0) {
            setCropSourceFile(null);
        }
    };

    const deleteFile = () => {
        setDropzoneVisible(true);
        setFiles([]);
        setEditImage(null);
        setEditFile(null);
        setCropSourceFile(null);
        onDeleteFile();
        setIsDelete(true);
    };

    useEffect(() => {
        return () => {
            files.forEach((file) => URL.revokeObjectURL(file.preview));
        };
    }, [files]);

    useEffect(() => {

        if (tryAgain) {
            setDropzoneVisible(true);
            setFiles([]);
            refreshTryAgain();
        }
    }, [tryAgain]);

    useEffect(() => {


        if (isEdit?.profileImage) {
            setEditImage(isEdit?.profileImage?.thumb);
            setDropzoneVisible(false);
        } else if (isEdit?.photoImage) {
            setEditImage(isEdit?.photoImage?.thumb || isEdit?.photoImage?.path);
            setDropzoneVisible(false);
        } else if (isEdit?.image) {
            setEditImage(isEdit.image);
            setEditFile(isEdit.fileName ? { name: isEdit.fileName, isImage: isEdit.isImage !== false } : null);
            setDropzoneVisible(false);
        } else if (isEdit?.avatar) {
            setEditImage(isEdit?.avatar);
            setDropzoneVisible(false);
        } else if (isEdit?.images) {
            setEditImage(isEdit?.thumb);
            setDropzoneVisible(false);
        } else {
            setEditImage(null);
            setEditFile(null);
            setFiles([]);
            setDropzoneVisible(true);
        }
    }, [isEdit]);

    useEffect(() => {

        if (resetState) {
            setEditImage(null);
            setFiles([]);
            setDropzoneVisible(true);
        }

    }, [resetState]);

    const renderThumbs = () => {
        if (editImage) {
            return (
                <Thumb key="edit-image" isProduct={product}>
                    <CloseThumb isProduct={product}>
                        <CloseThumbIcon onClick={deleteFile}>
                            <Icon name="close" color={colors.night} />
                        </CloseThumbIcon>
                    </CloseThumb>
                    <ThumbInner isProduct={product}>
                        {editFile && !editFile.isImage ? (
                            <ThumbFile>
                                <ThumbFileName title={editFile.name}>{editFile.name}</ThumbFileName>
                            </ThumbFile>
                        ) : (
                            <ThumbImage isProduct={product} src={editImage} />
                        )}
                    </ThumbInner>
                </Thumb>
            );
        } else if (files.length > 0) {
            return files.map((file) => (
                <Thumb key={file.name} isProduct={product} aspect={cropAspect}>
                    <CloseThumb isProduct={product}>
                        {cropAspect && cropSourceFile && (
                            <CropThumbIcon
                                type="button"
                                aria-label="Ajustar recorte da imagem"
                                onClick={() => setIsCropperOpen(true)}
                            >
                                <Crop size={16} color={colors.night} />
                            </CropThumbIcon>
                        )}
                        <CloseThumbIcon onClick={deleteFile}>
                            <Icon name="close" color={colors.night} />
                        </CloseThumbIcon>
                    </CloseThumb>
                    <ThumbInner isProduct={product}>
                        {/* PDF não renderiza em <img>: mostrar o nome do arquivo é o que
                           confirma para a pessoa que ela anexou o arquivo certo. */}
                        {file.type && file.type.startsWith('image/') ? (
                            <ThumbImage isProduct={product} src={file.preview} />
                        ) : (
                            <ThumbFile>
                                <ThumbFileName title={file.name}>{file.name}</ThumbFileName>
                            </ThumbFile>
                        )}
                    </ThumbInner>
                </Thumb>
            ));
        } else {
            return (
                <Dropzone {...getRootProps()} isProduct={product}>
                    <DropzoneContent>
                        <input {...getInputProps()} />
                        <IconContainer>
                            <Icon name={icon || 'camera'} color={colors.night} />
                        </IconContainer>
                        {dropzoneContent}
                    </DropzoneContent>
                </Dropzone>
            );
        }
    };

    return (
        <>
            <ThumbsContainer>{renderThumbs()}</ThumbsContainer>

            {cropAspect && (
                <ImageCropperModal
                    isOpen={isCropperOpen}
                    file={cropSourceFile}
                    aspect={cropAspect}
                    cropShape={cropShape}
                    title={cropTitle}
                    description={cropDescription}
                    onConfirm={handleCropConfirm}
                    onCancel={handleCropCancel}
                />
            )}
        </>
    );
}

const Dropzone = styled(FullyCentered)`
    width: 160px;
    height: 160px;
    padding: 15px;
    border: dashed 2px ${colors.nightLight};
    border-radius: 50%;
    background: ${colors.neutralLight};
    cursor: pointer;
    transition: all ease-in-out 0.25s;
    ${({ isProduct }) => isProduct && `
        width: 100%;
        height: 100%;
         border-radius: 4px;
    `}
    /* @media screen and (min-width: ${breakpoints.sm}) {
        margin-left: 10px;
    } */
    &:hover {
        border: dashed 2px ${colors.secondary};
    }
`;

const ThumbsContainer = styled(Row)`
    flex-wrap: wrap;
    justify-content: center;
    padding: 0 10px 0 10px;
`;

const Thumb = styled.div`
    display: inline-flex;
    border-radius: ${({ isProduct }) => (isProduct ? '4px' : '50%')};
    border: 1px solid ${colors.night};
    margin-bottom: 8px;
    width: ${({ isProduct }) => (isProduct ? '100%' : '160px')};
    height: 160px;
    padding: 4px;
    position: relative;

    /* Com recorte ativo a miniatura segue o aspecto escolhido, para o preview
       bater com o que foi enquadrado. */
    ${({ aspect }) => aspect && css`
        height: auto;
        aspect-ratio: ${aspect};
    `}
`;

const IconContainer = styled.div`
    width: 46px;
    height: 46px;
`;

const ThumbInner = styled.div`
    display: flex;
    min-width: 0;
    overflow: hidden;
    width: 100%;
    height: 100%;
    border-radius: ${({ isProduct }) => (isProduct ? '4px' : '50%')};
`;

const ThumbFile = styled(FullyCentered)`
    width: 100%;
    height: 100%;
    padding: 12px;
    background: ${colors.neutralLight};
`;

const ThumbFileName = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightDark};
    text-align: center;
    overflow-wrap: anywhere;
`;

const ThumbImage = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${({ isProduct }) => (isProduct ? '4px' : '50%')};
`;

const CloseThumb = styled(FullyCentered)`
    gap: 8px;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: ${colors.nightDark};
    transition: all ease-in-out 0.25s;
    opacity: 0.8;
    border-radius: ${({ isProduct }) => (isProduct ? '4px' : '50%')};
`;

const CloseThumbIcon = styled.div`
    width: 30px;
    height: 30px;
    background: ${colors.neutralDark};
    border-radius: 50%;
    padding: 6px;
    cursor: pointer;
`;

const CropThumbIcon = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background: ${colors.neutralDark};
    border: none;
    border-radius: 50%;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${colors.neutralLight};
        outline-offset: 2px;
    }
`;

const DropzoneContent = styled(FullyCentered)`
    flex-direction: column;
    padding: 30px;
`;

